/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import styles from './TopMenu.module.scss';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import MedPearlIcon from '../chat/components/Audience/MedPearl_logo.svg';
import MedPearlIconWithoutText from '../chat/components/Audience/MedPearl_logo_without_text.svg';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { selectPearlUserInfo, selectUserFlags } from '../Login/userSlice';
import { isMuscUser, isTeamsUser, useEnableEpicEmbeddedView, useIsConsumerWebUser } from '../permissions';
import {
  selectCreatorMode,
  selectDocumentVersionById,
  selectIsSearching,
  selectMessages,
  selectPublishedOnly,
} from '../chat/chatSlice';
import { FeedbackButton } from '../chat/components/Audience/AudienceComponent';
import QuickStartContent from '../sidemenu/QuickStartContent';
import {
  ConsumerLogoutIcon,
  ConsumerQuickStartIcon,
  CreatorIndexIcon,
  CreatorLogOutIcon,
  DownArrowIcon,
} from './MenuIcons';
import { useAuth } from '../Login/useHooks';
import MuscQuickStartPopover from '../musc/MuscQuickStartPopOver';
import Button from '@mui/material/Button';
import { useAudienceFromSearchParamsOrLocalStorage, useNavigateToTopic } from '../chat/useHooks';
import AutocompleteRenderer from '../Autocomplete/AutocompleteRenderer';
import { SearchInput } from '../chat/components/SearchInput';
import { toggleFeedback } from '../chat/DocumentVersionFeedback/feedbackSlice';
import { DigitalAssistantFeedbackTypes } from '../chat/DocumentVersionFeedback/FeedbackType';
import FeedbackForm from '../chat/DocumentVersionFeedback/FeedbackForm';
import { DocumentVersionType } from '../algorithm/types';
import Dialog from '@mui/material/Dialog';
import { CREATOR_FEEDBACK, LOGOUT, QUICK_START_GUIDE, SEARCH } from './menuUtils';

export type menuItemType = {
  title: string | JSX.Element;
  icon?: ReactNode;
  view: boolean | true;
  route?: string;
  action?: () => void;
  showBorder?: boolean | false;
};

type TopMenuProps = {
  children?: React.ReactNode;
  topMenuItems: menuItemType[];
};

function TopMenu({ topMenuItems, children }: TopMenuProps) {
  const location = useLocation();
  const showTopSearchBar = location.pathname === '/chat';
  const isLargeScreen = useMediaQuery('(min-width: 1000px)');
  const showIconWithText = useMediaQuery('(min-width:1240px)') || !showTopSearchBar;
  const navigate = useNavigate();
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const isTeamsAppUser = isTeamsUser(pearlUserInfo);
  const creatorMode = useAppSelector(selectCreatorMode);
  const messages = useAppSelector(selectMessages);
  const documentVersionId = useMemo(
    () => messages.find((m) => m.pages?.length)?.pages?.[0]?.document_version_id,
    [messages]
  );

  const documentVersion = useAppSelector((state) => selectDocumentVersionById(state, documentVersionId));
  const publishedOnly = useAppSelector(selectPublishedOnly);
  const flags = useAppSelector(selectUserFlags);
  const isSearching = useAppSelector(selectIsSearching);
  const inAdminPages = location.pathname.startsWith('/admin');

  const shouldUseAlgoliaAutocomplete = !creatorMode || flags?.creator_algolia_search;
  const [audienceFilter] = useAudienceFromSearchParamsOrLocalStorage(true, true);
  const navigateToTopic = useNavigateToTopic(publishedOnly);
  const sendText = useCallback(
    (text: string, slug?: string) => {
      if (text) navigateToTopic(text, slug, 'userTyped');
      return Promise.resolve();
    },
    [navigateToTopic]
  );

  const handleLogoClick = (e: React.MouseEvent) => {
    if (e.shiftKey) {
      navigate('/admin/logs');
      return;
    }
    if (location.pathname === '/' && creatorMode) return;
    navigate('/home#_');
  };

  return (
    <Box className={styles.box}>
      <div className={styles.topMenu} data-testid="topmenu-container">
        {(!showTopSearchBar || isLargeScreen) && pearlUserInfo && (
          <Tooltip title={location.pathname === '/' && creatorMode ? '' : 'click to return to home'} disableInteractive>
            <div className={styles.tooltipContainer}>
              <img
                onClick={handleLogoClick}
                className={styles.logo}
                style={showIconWithText ? undefined : { padding: '1px 0 0 1px' }} // There's an annoying 1px misalignment.
                src={showIconWithText ? MedPearlIcon : MedPearlIconWithoutText}
                alt="Logo - click to return to home"
              />
            </div>
          </Tooltip>
        )}
        {showTopSearchBar && (
          <div className={styles.searchBar} data-is-admin={inAdminPages ? 'true' : 'false'}>
            <div className={styles.searchAndActionsContainer} data-mode-creator="false">
              {/* use algolia autocomplete only if mode is consumer */}
              {shouldUseAlgoliaAutocomplete ? (
                <AutocompleteRenderer
                  onEnter={sendText}
                  publishedOnly={publishedOnly}
                  audience={audienceFilter}
                  className="chat-input-autocomplete"
                />
              ) : (
                <SearchInput
                  onEnter={sendText}
                  publishedOnly={publishedOnly}
                  audience={audienceFilter}
                  isSearching={isSearching}
                />
              )}
            </div>
          </div>
        )}
        <div className={styles.menuInfo}>
          {children}
          {pearlUserInfo && (
            <>
              <NavigationDropDownMenu topMenuItems={topMenuItems} documentVersion={documentVersion} />
              {!isTeamsAppUser && pearlUserInfo && !creatorMode && (
                <FeedbackButton compact={!isLargeScreen} documentVersion={documentVersion} />
              )}
            </>
          )}
        </div>
      </div>
      <div className={styles.scrollableBox}>
        <Outlet />
      </div>
    </Box>
  );
}

function NavigationDropDownMenu({
  topMenuItems,
  documentVersion,
}: {
  topMenuItems?: menuItemType[];
  documentVersion?: DocumentVersionType;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openQuickStart, setOpenQuickStart] = useState<boolean>(false);
  /* There's a slight issue with opening the quickstart logout from the menu as the Menu's paper blocks the Dialog paper.
  To fix this persistent warning, I'm opening the popups or performing any action on exiting the menu. */
  const navigate = useNavigate();
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const isUserFromMUSC = isMuscUser(pearlUserInfo);
  const location = useLocation();
  const { logout } = useAuth();
  const isCreatorMode = useAppSelector(selectCreatorMode);
  const isEpicEmbeddable = useEnableEpicEmbeddedView();
  const messageLength = useAppSelector(selectMessages)?.length;
  const [currentMenuItemLabel, setCurrentMenuItemLabel] = useState<string | JSX.Element | undefined>();
  const isConsumerWebUser = useIsConsumerWebUser();
  const [showFeedbackDialog, setShowFeedbackDialog] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const defaultFeedbackSelection =
    location.pathname === '/chat' && documentVersion
      ? DigitalAssistantFeedbackTypes.currentTopic
      : DigitalAssistantFeedbackTypes.newTopic;
  const logoutIcon = isCreatorMode ? <CreatorLogOutIcon /> : <ConsumerLogoutIcon />;
  const defaultMenuOptions: menuItemType[] = [
    {
      title: QUICK_START_GUIDE,
      view: !isCreatorMode,
      icon: <ConsumerQuickStartIcon />,
      showBorder: !isCreatorMode,
    },
    {
      title: LOGOUT,
      view: !isEpicEmbeddable,
      icon: logoutIcon,
    },
  ];
  useEffect(() => {
    const routeToFilter = location.pathname === '/home' ? SEARCH.route : location.pathname;
    const menuItem = topMenuItems?.filter((x) => x.route === routeToFilter)?.[0];
    const element = (
      <div className={styles.titleIconWrapper}>
        <div className={styles.activeIconWrapper}>{openQuickStart ? <ConsumerQuickStartIcon /> : menuItem?.icon}</div>
        {openQuickStart ? QUICK_START_GUIDE : menuItem?.title}
      </div>
    );

    setCurrentMenuItemLabel(element);
  }, [location.pathname, topMenuItems, openQuickStart]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setAnchorEl(null);
  };
  const closeFeedback = () => {
    dispatch(toggleFeedback());
    setShowFeedbackDialog(false);
  };
  async function handleMenuItemClick(e: any, menuItem: menuItemType): Promise<void> {
    (e.currentTarget as HTMLElement).blur();
    e.preventDefault();
    handleClose();

    if (menuItem.title === LOGOUT) {
      // Just adding a minor wait for smooth visual transition. This is barely noticable on fast networks.
      setTimeout(() => {
        logout();
      }, 50);
      return;
    }
    if (menuItem.title === CREATOR_FEEDBACK) {
      setShowFeedbackDialog((show) => !show);
      dispatch(toggleFeedback(defaultFeedbackSelection));
    }
    if (menuItem.route) {
      // If there are no open messages and the user clicks on search, navigate to home page instead of empty chat page.
      if (menuItem.title === SEARCH.label && messageLength < 1 && !isCreatorMode) {
        navigate('/home');
      } else navigate(menuItem.route);
      return;
    }
    if (menuItem.title === QUICK_START_GUIDE) {
      setOpenQuickStart(true);
      return;
    }
    if (menuItem.action) {
      menuItem.action();
    }
  }

  return (
    <>
      <Button
        onClick={handleClick}
        className={`${styles.menuButton} ${open ? styles.activeButton : ''}`}
        data-isconsumer={isConsumerWebUser ? 'true' : 'false'}
      >
        {isCreatorMode ? (
          <CreatorIndexIcon sx={{ color: 'var(--toplayout-menu-icon-color)' }} />
        ) : (
          <div className={styles.label}>
            <span>{currentMenuItemLabel}</span>
            {open ? (
              <DownArrowIcon
                sx={{ transform: 'rotate(180deg)', color: 'var(--messageresponse)' }}
                fontSize="small"
                data-testid="close-menu"
              />
            ) : (
              <DownArrowIcon fontSize="small" sx={{ color: 'var(--messageresponse)' }} data-testid="open-menu" />
            )}
          </div>
        )}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className={styles.navigationMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            className: styles.menuPaper,
          },
        }}
        MenuListProps={{
          className: styles.navigationMenuList,
        }}
        disableAutoFocus
        disableAutoFocusItem
        disableRestoreFocus
        data-testid="topmenu"
      >
        {topMenuItems?.map((menuItem: menuItemType, index) => {
          const isActive = menuItem.route === location.pathname;
          const showItem =
            isCreatorMode ||
            (menuItem.view && !isActive && !(menuItem.route === SEARCH.route && location.pathname === '/home'));

          return (
            showItem && (
              <MenuItem
                key={index}
                onMouseDown={(e) => e.preventDefault()}
                className={`${isActive ? styles.navigationMenuItemActive : ''} ${styles.navigationMenuItem}`}
                onClick={(e) => {
                  void handleMenuItemClick(e, menuItem);
                }}
                data-testid={`${isCreatorMode ? 'creator' : 'consumer'}-menu-${menuItem.title}`}
                data-show-border={menuItem?.showBorder ? 'true' : 'false'}
              >
                <span className={styles.iconWrapper}>{menuItem.icon}</span>
                {menuItem.title}
              </MenuItem>
            )
          );
        })}
        {defaultMenuOptions.map(
          (menuItem: menuItemType, index) =>
            menuItem.view && (
              <MenuItem
                key={`default-${menuItem.title}-${index}`}
                onMouseDown={(e) => e.preventDefault()}
                className={styles.navigationMenuItem}
                onClick={(e) => {
                  void handleMenuItemClick(e, menuItem);
                }}
                data-testid={`${isCreatorMode ? 'creator' : 'consumer'}-menu-default-${menuItem.title}`}
                data-show-border={menuItem?.showBorder ? 'true' : 'false'}
              >
                <span className={styles.iconWrapper}>{menuItem.icon}</span>
                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  {menuItem.title}
                  {menuItem.title === LOGOUT && (
                    <span style={{ fontSize: '12px', fontWeight: 400 }}>{pearlUserInfo?.email}</span>
                  )}
                </span>
              </MenuItem>
            )
        )}
      </Menu>
      {showFeedbackDialog && (
        <Dialog open={true} onClose={closeFeedback} PaperProps={{ style: { background: 'var(--home-background)' } }}>
          <FeedbackForm documentVersion={documentVersion} onClose={closeFeedback} isSmallScreen={true} />
        </Dialog>
      )}
      {/* Quick Start Dialog */}
      {openQuickStart &&
        (isUserFromMUSC ? (
          <MuscQuickStartPopover handleDialog={() => setOpenQuickStart(false)} />
        ) : (
          <QuickStartContent isCreatorMode={false} handleDialog={() => setOpenQuickStart(false)} />
        ))}
    </>
  );
}

export default TopMenu;
